import type {
  DocumentAgentMessage,
  DocumentAgentSessionRecord,
  DocumentType,
} from "@/types/documents";

export interface DocumentAgentSessionRepository {
  clear(): void;
  list(userId: string, documentType?: DocumentType): DocumentAgentSessionRecord[];
  read(userId: string, packId: string, documentType: DocumentType): DocumentAgentSessionRecord | null;
  upsert(userId: string, session: DocumentAgentSessionRecord): void;
}

const KEY_PREFIX = "applykitty.documents.agent-sessions";
const CACHE_VERSION = 2;
const MAX_SESSIONS = 12;
const MAX_MESSAGES = 80;

interface CachedSessions {
  version: number;
  sessions: DocumentAgentSessionRecord[];
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function key(userId: string): string {
  return `${KEY_PREFIX}:${userId}`;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isMessage(value: unknown): value is DocumentAgentMessage {
  if (!value || typeof value !== "object") return false;
  const message = value as Partial<DocumentAgentMessage>;
  return isString(message.id)
    && isString(message.runId)
    && ["request", "tool-call", "tool-result", "assistant", "error"].includes(message.kind ?? "")
    && isString(message.title)
    && isString(message.detail)
    && isString(message.createdAt);
}

function isDocumentType(value: unknown): value is DocumentType {
  return value === "cv" || value === "cover_letter";
}

function isSession(value: unknown): value is DocumentAgentSessionRecord {
  if (!value || typeof value !== "object") return false;
  const session = value as Partial<DocumentAgentSessionRecord>;
  return isString(session.id)
    && isString(session.packId)
    && isDocumentType(session.documentType)
    && ["idle", "running", "completed", "failed"].includes(session.status ?? "")
    && Array.isArray(session.messages)
    && session.messages.every(isMessage)
    && isString(session.updatedAt);
}

function cloneSession(session: DocumentAgentSessionRecord): DocumentAgentSessionRecord {
  return {
    ...session,
    messages: session.messages.slice(-MAX_MESSAGES).map((message) => ({ ...message })),
  };
}

function readAll(userId: string): DocumentAgentSessionRecord[] {
  const sessionStorage = storage();
  if (!sessionStorage || !userId) return [];
  const storageKey = key(userId);
  try {
    const parsed = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as Partial<CachedSessions> | null;
    if (!parsed || parsed.version !== CACHE_VERSION || !Array.isArray(parsed.sessions)) return [];
    if (!parsed.sessions.every(isSession)) {
      sessionStorage.removeItem(storageKey);
      return [];
    }
    return parsed.sessions.slice(0, MAX_SESSIONS).map(cloneSession);
  } catch {
    sessionStorage.removeItem(storageKey);
    return [];
  }
}

function writeAll(userId: string, sessions: DocumentAgentSessionRecord[]): void {
  const sessionStorage = storage();
  if (!sessionStorage || !userId) return;
  try {
    const cached: CachedSessions = {
      version: CACHE_VERSION,
      sessions: sessions.slice(0, MAX_SESSIONS).map(cloneSession),
    };
    sessionStorage.setItem(key(userId), JSON.stringify(cached));
  } catch {
    // The Pinia session remains authoritative when browser storage is unavailable.
  }
}

export const documentAgentSessionRepository: DocumentAgentSessionRepository = {
  read(userId, packId, documentType) {
    return readAll(userId).find((session) => (
      session.packId === packId && session.documentType === documentType
    )) ?? null;
  },
  list(userId, documentType) {
    return readAll(userId).filter((session) => !documentType || session.documentType === documentType);
  },
  upsert(userId, record) {
    const sessions = readAll(userId).filter((session) => (
      session.packId !== record.packId || session.documentType !== record.documentType
    ));
    writeAll(userId, [cloneSession(record), ...sessions]);
  },
  clear() {
    const sessionStorage = storage();
    if (!sessionStorage) return;
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const storageKey = sessionStorage.key(index);
        if (storageKey?.startsWith(`${KEY_PREFIX}:`)) sessionStorage.removeItem(storageKey);
      }
    } catch {
      // Session reset still clears Pinia state if storage access is restricted.
    }
  },
};
